import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/ueber-uns", label: "Über uns" },
  { to: "/schreinerei", label: "Schreinerei" },
  { to: "/bestattungen", label: "Bestattungen" },
  { to: "/kontakt", label: "Kontakt" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  const isLight = !scrolled && isHome;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled || !isHome
          ? "backdrop-blur-xl bg-[#F8F5F0]/92 shadow-sm border-b border-[#8B5E3C]/10"
          : "bg-gradient-to-b from-black/30 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
        <Link to="/" data-testid="logo-link" className="flex flex-col leading-none">
          <span
            className={`font-playfair text-2xl font-semibold tracking-[0.25em] transition-colors duration-300 ${
              isLight ? "text-white" : "text-[#8B5E3C]"
            }`}
          >
            LOOS
          </span>
          <span
            className={`text-[10px] tracking-[0.25em] uppercase font-manrope transition-colors duration-300 ${
              isLight ? "text-white/60" : "text-[#2B2B2B]/40"
            }`}
          >
            Haltern am See
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
              className={`text-sm font-manrope tracking-wide pb-0.5 transition-all duration-300 ${
                location.pathname === link.to
                  ? isLight
                    ? "text-white border-b border-white"
                    : "text-[#8B5E3C] border-b border-[#8B5E3C]"
                  : isLight
                  ? "text-white/80 hover:text-white"
                  : "text-[#2B2B2B]/65 hover:text-[#8B5E3C]"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Mobile toggle */}
        <button
          className={`md:hidden transition-colors ${isLight ? "text-white" : "text-[#2B2B2B]"}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Menü öffnen"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden bg-[#F8F5F0]/98 backdrop-blur-xl border-t border-[#8B5E3C]/10 overflow-hidden"
          >
            <nav className="flex flex-col px-6 py-4 gap-1">
              {LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`font-manrope text-sm py-3 border-b border-[#8B5E3C]/10 transition-colors ${
                    location.pathname === link.to ? "text-[#8B5E3C] font-semibold" : "text-[#2B2B2B]/75 hover:text-[#8B5E3C]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
